import React, { useState } from 'react';
import { PlayCircle, Mail, CheckCircle, Loader2, ArrowLeft, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../utils/api';

const DemoRegister = () => {
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    // Get token from URL: /demo-register?token=xxx
    const token = new URLSearchParams(window.location.search).get('token');

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        if (!token) { setError('Invalid registration link. Please use the link provided by your administrator.'); return; }

        setSubmitting(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE_URL}/api/demo/self-register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim(), token }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Registration failed.');
            setSuccess(true);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6 text-center">
                    <PlayCircle size={40} className="text-white mx-auto mb-3" />
                    <h1 className="text-2xl font-bold text-white">Cogent Platform</h1>
                    <p className="text-blue-100 mt-1 text-sm">Demo Account Registration</p>
                </div>

                <div className="p-8">
                    {success ? (
                        <div className="text-center py-4">
                            <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                            <h2 className="text-xl font-bold text-gray-800 mb-2">Registration Successful</h2>
                            <p className="text-gray-600 text-sm mb-4">
                                Your demo account has been created. A welcome email with sign-in instructions has been sent to <strong>{email}</strong>.
                            </p>
                            <p className="text-gray-500 text-xs mb-6">
                                Please check your inbox (and spam folder) for the login link. You will have 5 days of full access to the platform with 4 pre-configured mock clients. Client creation is disabled in demo mode.
                            </p>
                            <Link to="/login" className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold">
                                Go to Sign In
                            </Link>
                        </div>
                    ) : !token ? (
                        <div className="text-center py-4">
                            <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
                            <h2 className="text-lg font-bold text-gray-800 mb-2">Invalid Registration Link</h2>
                            <p className="text-gray-500 text-sm">This link is missing or invalid. Please contact your administrator for a valid demo registration link.</p>
                        </div>
                    ) : (
                        <>
                            <p className="text-gray-600 text-sm mb-6">
                                Register for a free demo introducing agent account. You'll get access to the full platform with 4 mock clients to explore trading, portfolio management, and more.
                            </p>

                            <form onSubmit={handleSubmit}>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                                    <div className="relative">
                                        <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            placeholder="your@email.com"
                                            required
                                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                {error && (
                                    <div className="mb-4 bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{error}</div>
                                )}

                                <button type="submit" disabled={submitting}
                                    className="w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-semibold flex items-center justify-center gap-2">
                                    {submitting ? <Loader2 size={16} className="animate-spin" /> : <PlayCircle size={16} />}
                                    {submitting ? 'Creating Account...' : 'Register for Demo'}
                                </button>
                            </form>

                            <div className="mt-6 pt-4 border-t border-gray-100 text-xs text-gray-400 space-y-1">
                                <p>- 5-day access to the full introducing agent dashboard</p>
                                <p>- 4 pre-configured mock clients with different risk profiles</p>
                                <p>- Real instrument prices, simulated trades</p>
                                <p>- Client creation is disabled — demo uses pre-set mock clients only</p>
                                <p>- No credit card required</p>
                            </div>
                        </>
                    )}

                    <div className="mt-6 text-center">
                        <Link to="/" className="text-xs text-gray-400 hover:text-gray-600 inline-flex items-center gap-1">
                            <ArrowLeft size={12} /> Back to Cogent Platform
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DemoRegister;
